import * as React from 'react'
import { Provider } from 'react-redux'
import { FlashMessagesProvider } from '@axilon-apps/libs-ui-components'
import { RouterExposer } from '@axilon-apps/libs-ui-core'
import {
  SettingsProvider,
  ThemeProvider,
  applyDefaultSettings,
  getSettings as getPersistedSettings
} from '@axilon-apps/libs-ui-dashboard'
import { PlatformProvider } from '@axilon-apps/domains-identity/platform'
import { OidcAuthProvider } from '@axilon-apps/domains-identity/web'
import { api as identityApi, hasTokenSelector } from '@axilon-apps/domains-identity/state'
import { store } from '@axilon-apps/domains-ontology-ui-state'
import webPlatformServices from '../../platform/web-platform-services'
import { DocumentUploadProvider } from '../IngestSession/DocumentUploadProvider'
import { FlagSnapshotProvider } from '../flags/FlagSnapshotProvider'
import { Toaster } from '../ui/sonner'

interface RootProps {
  children: React.ReactNode
}

export function Root({ children }: RootProps): React.JSX.Element {
  const settings = React.useRef(applyDefaultSettings(getPersistedSettings()))

  // Load the current user once at boot when a session token was restored
  // from storage. The identity api's getCurrentUser side-effect populates
  // the authentication slice; AuthenticationGuard waits on this query.
  React.useEffect(() => {
    if (hasTokenSelector(store.getState())) {
      store.dispatch(identityApi.endpoints.getCurrentUser.initiate())
    }
  }, [])

  return (
    <PlatformProvider services={webPlatformServices}>
      <Provider store={store}>
        <OidcAuthProvider>
          <SettingsProvider settings={settings.current}>
            <FlashMessagesProvider>
              <ThemeProvider>
                <DocumentUploadProvider>
                  <FlagSnapshotProvider>{children}</FlagSnapshotProvider>
                </DocumentUploadProvider>
                <RouterExposer />
                <Toaster closeButton richColors position="bottom-right" />
              </ThemeProvider>
            </FlashMessagesProvider>
          </SettingsProvider>
        </OidcAuthProvider>
      </Provider>
    </PlatformProvider>
  )
}
